import { useEffect, useState } from "react";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Eye,
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  MapPin,
  Clock,
  ExternalLink,
  RefreshCw,
  MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import { VisitorNotifSettings } from "@/components/VisitorNotifSettings";

interface Visitor {
  session_id: string;
  ip: string;
  device: string;
  browser: string;
  page: string;
  referrer: string;
  screen_width: number;
  duration_seconds: number;
  geo: { country?: string; city?: string; region?: string };
  started_at: string;
  last_seen: string;
  pages_count: number;
}

interface Stats {
  live: number;
  today_new: number;
  total_tracked: number;
}

const deviceIcon = (d: string) => {
  if (d === "mobile") return <Smartphone className="h-4 w-4" />;
  if (d === "tablet") return <Tablet className="h-4 w-4" />;
  return <Monitor className="h-4 w-4" />;
};

const fmtDuration = (s: number) => {
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m ${r}s`;
};

const fmtReferrer = (r: string) => {
  if (!r) return "Accès direct";
  try {
    const u = new URL(r);
    return u.hostname.replace(/^www\./, "");
  } catch {
    return r.slice(0, 30);
  }
};

export default function LiveVisitorsPage() {
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const load = async () => {
    try {
      const [vRes, sRes] = await Promise.all([
        fetch(`${BACKEND}/api/visitors/live`, { credentials: "include" }),
        fetch(`${BACKEND}/api/visitors/stats`, { credentials: "include" }),
      ]);
      if (vRes.ok) {
        const data = await vRes.json();
        setVisitors(data.visitors || []);
      }
      if (sRes.ok) {
        setStats(await sRes.json());
      }
      // Mark notifications as seen when admin views this page
      await fetch(`${BACKEND}/api/visitors/events/mark-seen`, {
        method: "POST",
        credentials: "include",
      });
    } catch {
      /* noop */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    if (!autoRefresh) return;
    const i = setInterval(load, 10000); // auto-refresh every 10s
    return () => clearInterval(i);
  }, [autoRefresh]);

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <DashboardSidebar />
        <main className="flex-1 p-6 md:p-10 overflow-y-auto">
          <div className="max-w-6xl mx-auto space-y-8">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3" data-testid="live-visitors-title">
                  <Eye className="h-7 w-7 text-primary" />
                  Visiteurs en direct
                </h1>
                <p className="text-muted-foreground mt-1">
                  Voyez en temps réel qui visite votre site Grafton IA. Auto-refresh toutes les 10s.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setAutoRefresh(!autoRefresh);
                  toast.success(autoRefresh ? "Auto-refresh désactivé" : "Auto-refresh activé");
                }}
                data-testid="visitors-auto-refresh-toggle"
              >
                <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${autoRefresh ? "animate-spin-slow" : ""}`} />
                Auto-refresh {autoRefresh ? "ON" : "OFF"}
              </Button>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card data-testid="kpi-live">
                <CardContent className="pt-5">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-md bg-emerald-500/15 flex items-center justify-center">
                      <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-emerald-400">{stats?.live ?? 0}</div>
                      <div className="text-xs text-muted-foreground">En ligne maintenant</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card data-testid="kpi-today">
                <CardContent className="pt-5">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-md bg-primary/15 flex items-center justify-center">
                      <Eye className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{stats?.today_new ?? 0}</div>
                      <div className="text-xs text-muted-foreground">Nouveaux aujourd'hui</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card data-testid="kpi-total">
                <CardContent className="pt-5">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-md bg-muted/40 flex items-center justify-center">
                      <Globe className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{stats?.total_tracked ?? 0}</div>
                      <div className="text-xs text-muted-foreground">Sessions totales (toutes périodes)</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Notification settings */}
            <VisitorNotifSettings />

            {/* Visitors list */}
            <Card data-testid="visitors-list">
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>Sessions actives ({visitors.length})</span>
                  {loading && <span className="text-xs text-muted-foreground">Chargement…</span>}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {visitors.length === 0 ? (
                  <div className="text-center py-10 text-muted-foreground space-y-2">
                    <Eye className="h-8 w-8 mx-auto opacity-50" />
                    <div>Aucun visiteur en ligne pour le moment.</div>
                    <p className="text-xs">
                      Partagez votre lien <code>{window.location.origin}</code> et regardez la magie opérer.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {visitors.map((v) => (
                      <div
                        key={v.session_id}
                        className="border border-border/60 rounded-lg p-4 hover:border-primary/40 transition-colors"
                        data-testid={`visitor-${v.session_id}`}
                      >
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <div className="h-10 w-10 rounded-md bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="outline" className="gap-1 text-xs">
                                  {deviceIcon(v.device)}
                                  {v.device}
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {v.browser}
                                </Badge>
                                {v.geo?.city && (
                                  <Badge variant="outline" className="gap-1 text-xs">
                                    <MapPin className="h-3 w-3" />
                                    {v.geo.city}
                                    {v.geo.country ? `, ${v.geo.country}` : ""}
                                  </Badge>
                                )}
                                <Badge variant="outline" className="gap-1 text-xs text-amber-400 border-amber-500/30">
                                  <Clock className="h-3 w-3" />
                                  {fmtDuration(v.duration_seconds || 0)}
                                </Badge>
                              </div>
                              <div className="text-sm text-muted-foreground mt-1.5">
                                Page : <span className="font-mono text-foreground">{v.page}</span>
                              </div>
                              <div className="text-xs text-muted-foreground mt-0.5">
                                Source : {fmtReferrer(v.referrer)} · IP {v.ip || "—"} · {v.screen_width}px
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            <Button
                              size="sm"
                              variant="default"
                              data-testid={`visitor-engage-${v.session_id}`}
                              onClick={() =>
                                toast.info(
                                  "Chat live à venir — pour l'instant, ouvrez le chat public de la landing pour engager.",
                                )
                              }
                              className="gap-1.5"
                            >
                              <MessageCircle className="h-3.5 w-3.5" /> Engager
                            </Button>
                            {v.page && (
                              <Button
                                size="sm"
                                variant="outline"
                                asChild
                                data-testid={`visitor-open-${v.session_id}`}
                              >
                                <a href={v.page} target="_blank" rel="noreferrer">
                                  <ExternalLink className="h-3.5 w-3.5 mr-1" /> Page
                                </a>
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
